import { useEffect, useState } from "react";

import {
  listLiveCatalog,
  type AccessScope,
} from "@/lib/api/access-requests";
import type {
  LiveCatalogLocation,
  LiveCatalogNamespace,
} from "@/lib/api/live-catalog";

const CATALOG_UNAVAILABLE: Record<AccessScope, string> = {
  namespaces:
    "Live namespaces are unavailable right now. Try again in a few minutes.",
  locations:
    "Live locations are unavailable right now. Try again in a few minutes.",
};

export const MIN_CATALOG_SEARCH_LENGTH = 3;

export function useLiveCatalogSearch(
  query: string,
  mode: AccessScope,
): {
  namespaces: LiveCatalogNamespace[];
  locations: LiveCatalogLocation[];
  loading: boolean;
  error: string | null;
} {
  const needle = query.trim();
  const active = [...needle].length >= MIN_CATALOG_SEARCH_LENGTH;
  const [namespaces, setNamespaces] = useState<LiveCatalogNamespace[]>([]);
  const [locations, setLocations] = useState<LiveCatalogLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [trackedNeedle, setTrackedNeedle] = useState(needle);
  const [trackedActive, setTrackedActive] = useState(active);

  if (needle !== trackedNeedle || active !== trackedActive) {
    setTrackedNeedle(needle);
    setTrackedActive(active);
    setUnavailable(false);
    if (!active) {
      setNamespaces([]);
      setLocations([]);
      setLoading(false);
    } else {
      setLoading(true);
    }
  }

  useEffect(() => {
    if (!active) {
      return;
    }

    let cancelled = false;
    listLiveCatalog(needle, mode)
      .then((next) => {
        if (!cancelled) {
          setNamespaces(next.namespaces ?? []);
          setLocations(next.locations ?? []);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setNamespaces([]);
          setLocations([]);
          setUnavailable(true);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [active, needle, mode]);

  return {
    namespaces,
    locations,
    loading,
    error: active && unavailable ? CATALOG_UNAVAILABLE[mode] : null,
  };
}
